<template>
  <v-dialog
    @keydown.esc="dialogType = 0"
    :value="!!dialogType"
    content-class="media-dialog"
  >
    <div class="d-flex justify-end">
      <v-btn
        color="bh-white"
        small
        rounded
        height="48"
        width="48"
        min-width="48"
        max-width="48"
        elevation="0"
        class="mt-6 mr-6 pa-0 close-button"
        @click="closeMediaDialog"
      >
        <v-icon color="accent">mdi-close</v-icon>
      </v-btn>
    </div>
    <v-row class="flex-column" no-gutters align="center" justify="center">
      <div v-if="dialogType == 2 || dialogType == 1">
        <v-img
          v-if="imageFileUrl"
          class="media-dialog-image"
          :src="imageFileUrl"
          :key="imageFileUrl"
          min-width="32"
          min-height="32"
          max-height="80vh"
          max-width="90vw"
          contain
        >
          <template v-slot:placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular
                indeterminate
                color="white"
              ></v-progress-circular>
            </v-row>
          </template>
        </v-img>
        <v-row v-else class="fill-height ma-0" align="center" justify="center">
          <v-progress-circular
            indeterminate
            color="white"
          ></v-progress-circular>
        </v-row>
      </div>
      <div
        v-if="dialogType === 3 || dialogType === 4"
        style="min-width: 550px; max-width: 550px;"
        class="d-flex justify-center align-center flex-column py-4"
      >
        <video
          :ref="'video-'"
          height="540"
          width="960"
          controls=""
          name="media"
          @error="videoError(dialogFileMlUrl, 'video-')"
        >
          <source :src="imageSrc" :type="`video/mp4`" />
        </video>
      </div>
      <div
        v-if="dialogType === 5 || dialogType === 6"
        class="media-dialog-pdf-wrapper"
      >
        <div class="media-dialog-pdf-nav" v-if="!pdfLoading">
          <v-btn
            color="accent"
            elevation="0"
            small
            @click="prevPage"
            :disabled="currentPage <= 1"
          >
            <v-icon>bhi-chevron-left</v-icon>
          </v-btn>
          <span v-if="currentPage && pageCount"
            >{{ currentPage }} / {{ pageCount }}</span
          >
          <v-btn
            color="accent"
            elevation="0"
            small
            @click="nextPage"
            :disabled="currentPage == pageCount"
          >
            <v-icon>bhi-chevron-right</v-icon>
          </v-btn>
        </div>
        <vue-pdf-embed
          v-if="pdfUrl"
          @error="showPdfError"
          @loaded="pdfLoaded"
          :source="pdfUrl"
          @page-loaded="pdfPageLoaded"
          :annotation-layer="true"
          :text-layer="true"
          :page="currentPage"
          @internal-link-clicked="pdfInternalLinkClicked"
          @rendered="pdfRendered"
        />
        <v-progress-circular
          indeterminate
          :color="dialogType === 6 ? 'white' : 'accent'"
          v-if="pdfLoading || !pdfUrl"
        ></v-progress-circular>
      </div>
      <div
        v-if="dialogType === 2 || dialogType === 4 || dialogType === 6"
        style="min-width: 550px; max-width: 550px;"
        class="d-flex justify-center align-center flex-column py-4"
      >
        <v-text-field
          style="width: 100%;"
          label="Your comment (optional)"
          v-model="currentMessageText"
          solo
          autofocus
          @input="$emit('change-message', $event)"
          @keydown.enter.prevent="addMessage"
          :disabled="sendingMessage"
        />
        <p v-if="sendingMessage" class="white--text font-weight-medium">
          Please wait, uploading file...
        </p>
        <v-btn
          color="primary"
          fab
          large
          elevation="0"
          @click="addMessage"
          :loading="sendingMessage"
        >
          <v-icon size="24">mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </v-row>
    <div
      v-if="dialogType === 1 || dialogType === 3 || dialogType === 5"
      class="d-flex justify-center"
    >
      <v-btn
        class="media-dialog-download-btn mb-6"
        color="white"
        elevation="0"
        rounded
        @click="downloadFileHandler"
      >
        <span class="media-dialog-download-btn-text">download</span>
        <v-icon right color="accent">bhi-download</v-icon>
      </v-btn>
    </div>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed'

export default {
  name: 'PreviewZoomDialog',
  components: {
    VuePdfEmbed,
  },
  props: {
    dialogType: {
      type: Number,
      default: 0,
    },
    dialogFile: {
      type: String,
      default: '',
    },
    imageSrc: {
      type: [Boolean, String],
      default: false,
    },
    dialogFileMlUrl: {
      type: [Boolean, String, Number],
      default: false,
    },
    name: {
      type: [Boolean, String],
      default: false,
    },
    messageText: {
      type: String,
      default: '',
    },
    sendingMessage: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    dialogType(value) {
      if (value === 0 && (this.currentFile || this.imageSrc)) {
        this.closeMediaDialog()
      } else if (value === 5 || value === 6) {
        this.pdfLoading = true
      }
    },
    messageText(value) {
      this.currentMessageText = value
    },
  },
  data() {
    return {
      currentFile: null,
      currentMessageText: '',
      pdfLoading: false,
      currentPage: 1,
      pageCount: 0,
      scale: 1, // window scale
    }
  },
  computed: {
    pdfUrl() {
      if (this.imageSrc && typeof this.imageSrc === 'string') {
        return this.imageSrc
      }
      return null
    },
    imageFileUrl() {
      if (this.dialogFile && typeof this.dialogFile === 'string') {
        return this.dialogFile
      }
      if (this.imageSrc && typeof this.imageSrc === 'string') {
        return this.imageSrc
      }
      return null
    },
  },
  methods: {
    ...mapActions('resourcesStates', [
      'downloadResourceByUrl',
      'checkFileAvailable',
      'getResourceInfo',
    ]),
    ...mapActions('chatStates', ['downloadFile']),
    updateScale() {
      this.scale = window.devicePixelRatio || 1
    },
    closeMediaDialog() {
      this.$emit('close-dialog')
      this.pdfLoading = false
      this.currentPage = 1
    },
    addMessage() {
      this.$emit('add-message', this.currentMessageText)
    },
    downloadFileHandler(retry = true) {
      this.downloading = true
      let callback = this.checkFileCallback
      if (!retry) callback = false
      if (this.imageSrc) {
        this.checkFileAvailable({
          url: this.imageSrc,
          dialogFileMlUrl: this.dialogFileMlUrl,
          name: this.name,
          cb: callback,
        })
      } else {
        this.downloadFile(this.dialogFile)
        this.downloading = false
      }
    },
    checkFileCallback(resourceUrl) {
      this.downloading = false
      if (!resourceUrl) return
      this.$emit('reload-resource', resourceUrl)
      // try once 5s after error
      setTimeout(() => {
        this.downloadFileHandler(false)
      }, 5000)
    },
    pdfLoaded(event) {
      this.pageCount = event?.numPages || 1
      this.pdfLoading = false
    },
    async videoError(url, id) {
      await this.getResourceInfo(this.mediaObj.mlResourceUrl)
      this.$refs[id].load()
      this.$refs[id].play()
    },
    nextPage() {
      if (this.currentPage < this.pageCount) this.currentPage++
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--
    },
    showPdfError(error) {
      console.error('pdf load error', error)
    },
    pdfInternalLinkClicked(page) {
      if (page < this.pageCount) {
        this.currentPage = page
      }
    },
    pdfRendered() {
      const links = document.querySelectorAll('.annotationLayer a')
      links.forEach(link => {
        const linkHasHost = link.host && link.host !== ''
        const linkHasHostname = link.hostname && link.hostname !== ''
        const linkHasAnyHost = linkHasHost || linkHasHostname
        const isLinkInternal = link.className.includes('internalLink')
        const isForcedNewWindow = link.getAttribute('target') === '_blank'
        if (!isLinkInternal && linkHasAnyHost && !isForcedNewWindow) {
          link.setAttribute('target', '_blank')
        }
      })
    },
  },
  mounted() {
    this.updateScale()
    window.addEventListener('resize', this.updateScale)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScale)
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';
video {
  background: #000000;
}
.media-dialog {
  .close-button {
    font-size: 16px;
    .v-icon {
      font-size: 24px;
    }
  }
  &-pdf {
    &-wrapper {
      max-width: 80%;
      max-height: 70vh;
      flex-grow: 1;
      padding: 15px;
      background: $bh-white;
      overflow: auto;
      margin: 0 auto 50px;
      width: 100%;
      position: relative;
      .v-progress-circular {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate3d(-50%, -50%, 0);
        z-index: 0;
      }
      & > span {
        z-index: 1;
      }
    }
    &-nav {
      margin-bottom: 10px;
      & > span {
        margin: 0 15px;
      }
    }
  }
}
</style>
